import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import Papa from "papaparse";
import "./TrialBalance.css";

const TrialBalance = () => {
  const { companyId } = useCompany();

  // Filters
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // Data/UI
  const [trialBalanceData, setTrialBalanceData] = useState([]);
  const [totalDebit, setTotalDebit] = useState("0.00");
  const [totalCredit, setTotalCredit] = useState("0.00");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Default to YTD
  useEffect(() => {
    const year = new Date().getFullYear();
    setFilterStartDate(`${year}-01-01`);
    setFilterEndDate(new Date().toISOString().split("T")[0]);
  }, []);

  // Optional: keep trial_balance table in sync (now efficient: uses account_id directly)
  const updateTrialBalance = useCallback(
    async (rows) => {
      if (!companyId || !rows?.length) return;

      // NOTE:
      // For upsert to work properly, ensure you have a UNIQUE constraint or index on:
      // trial_balance(company_id, account_id)
      //
      // Example:
      // CREATE UNIQUE INDEX IF NOT EXISTS trial_balance_company_account_ux
      // ON public.trial_balance(company_id, account_id);

      const payload = rows
        .filter((r) => r.account_id)
        .map((r) => ({
          company_id: companyId,
          account_id: r.account_id,
          total_debit: Number(r.debit) || 0,
          total_credit: Number(r.credit) || 0,
        }));

      if (!payload.length) return;

      const { error } = await supabase
        .from("trial_balance")
        .upsert(payload, { onConflict: "company_id,account_id" });

      if (error) {
        console.warn("TB sync warning:", error);
      }
    },
    [companyId]
  );

  // Fetch and compute trial balance (✅ SQL aggregation, no 1000-row cap)
  const fetchTrialBalance = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setErr("");

    try {
      const start = filterStartDate || null;
      const end = filterEndDate || null;

      const { data: rows = [], error } = await supabase.rpc(
        "get_trial_balance",
        {
          p_company_id: companyId,
          p_start_date: start,
          p_end_date: end,
        }
      );

      if (error) throw error;

      const formatted = (rows || [])
        .map((r) => ({
          account_id: r.account_id,
          account_name: r.account_name || "Unknown",
          type: r.account_type || "N/A",
          debit: (Number(r.total_debit) || 0).toFixed(2),
          credit: (Number(r.total_credit) || 0).toFixed(2),
        }))
        .sort((a, b) =>
          a.type === b.type
            ? a.account_name.localeCompare(b.account_name)
            : a.type.localeCompare(b.type)
        );

      const totalDebits = formatted.reduce(
        (s, r) => s + parseFloat(r.debit),
        0
      );
      const totalCredits = formatted.reduce(
        (s, r) => s + parseFloat(r.credit),
        0
      );

      setTrialBalanceData(formatted);
      setTotalDebit(totalDebits.toFixed(2));
      setTotalCredit(totalCredits.toFixed(2));

      // Keep DB table in sync (optional)
      updateTrialBalance(formatted);
    } catch (e) {
      console.error("Trial Balance fetch error:", e);
      setErr("Failed to compute trial balance.");
    } finally {
      setLoading(false);
    }
  }, [companyId, filterStartDate, filterEndDate, updateTrialBalance]);

  useEffect(() => {
    fetchTrialBalance();
  }, [fetchTrialBalance]);

  const exportToCSV = () => {
    if (!trialBalanceData.length) {
      alert("No rows to export.");
      return;
    }
    const csv = Papa.unparse(
      trialBalanceData.map((r) => ({
        Account: r.account_name,
        Type: r.type,
        "Debit (₦)": r.debit,
        "Credit (₦)": r.credit,
      }))
    );
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "trial_balance.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="tb-container">
      <div className="tb-header">
        <div>
          <h2 className="tb-title">Trial Balance</h2>
          <p className="tb-sub">
            From {filterStartDate || "…"} to {filterEndDate || "…"}
          </p>
        </div>
        <button className="tb-primary" onClick={exportToCSV}>
          Export CSV
        </button>
      </div>

      <div className="tb-filter-card">
        <div className="tb-row">
          <div className="input-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </div>
          <div className="input-group">
            <label>End Date</label>
            <input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </div>
          <div className="input-group">
            <label>&nbsp;</label>
            <button className="tb-primary" onClick={fetchTrialBalance}>
              Apply Filter
            </button>
          </div>
        </div>
        {err && <p className="tb-error">{err}</p>}
      </div>

      <div className="tb-card">
        {loading ? (
          <div className="tb-loading">Loading…</div>
        ) : (
          <div className="tb-table-wrap">
            <table className="tb-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Type</th>
                  <th className="num">Debit (₦)</th>
                  <th className="num">Credit (₦)</th>
                </tr>
              </thead>
              <tbody>
                {trialBalanceData.length ? (
                  trialBalanceData.map((entry, i) => (
                    <tr key={`${entry.account_id}-${i}`}>
                      <td>{entry.account_name}</td>
                      <td className="dim">{entry.type}</td>
                      <td className="num">
                        {parseFloat(entry.debit).toLocaleString()}
                      </td>
                      <td className="num">
                        {parseFloat(entry.credit).toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="empty">
                      No transactions found for the selected dates.
                    </td>
                  </tr>
                )}
              </tbody>

              {/* ✅ totals restored */}
              {trialBalanceData.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="2" className="total-label">
                      <strong>Total</strong>
                    </td>
                    <td className="num">
                      <strong>{parseFloat(totalDebit).toLocaleString()}</strong>
                    </td>
                    <td className="num">
                      <strong>
                        {parseFloat(totalCredit).toLocaleString()}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrialBalance;
